import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import IconAndText from 'components/common/IconAndText'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { ROUTES } from 'consts'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { trackEvent } from 'utils/analyticsUtils'
import { isMobile } from 'utils/mobileUtils'
import CopilotSettingsTooltip from './CopilotSettingsTooltip'
import GreenDot from './icons/CopilotSettings/GreenDot'

const useStyles = makeStyles(theme => ({
  copilotSettingItem: {
    height: 80,
    backgroundColor: 'rgba(242, 244, 255, 1)',
    padding: 12,
    borderRadius: 16,
    position: 'relative',
    cursor: 'pointer',
    width: '160px',
    [theme.breakpoints.up('lg')]: {
      width: '160px'
    },
    [theme.breakpoints.up('xl')]: {
      width: '220px'
    },
    // On phones the parent Grid item gives each card 50% of the row; let the card
    // fill that slot instead of staying clamped at 160px.
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  summaryLabel: {
    color: 'rgba(138, 153, 182, 1)',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '150px',
    [theme.breakpoints.up('xl')]: {
      maxWidth: '200px'
    },
    // Phones: the card is full-width of its Grid item — let the summary ellipsis
    // use that whole width instead of the desktop's 150px clamp.
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%'
    }
  },
  greenDot: {
    position: 'absolute',
    top: 10,
    right: 10
  }
}))

/**
 *
 * @param {Object} props
 * @param {JSX.Element} props.icon - The icon to display.
 * @param {string} props.title - The title of the setting item.
 * @param {string} [props.summary] - The summary text to display.
 * @param {function} props.onClick - The function to call when the item is clicked.
 * @param {CopilotSettingsCategory} [props.categoryData] - The data for the category, used for the tooltip.
 * @param {Object} [props.analyticsPayload] - Optional analytics payload.
 */
const CopilotSettingItem = ({ icon, title, summary, onClick, categoryData = {}, analyticsPayload = {} }) => {
  const classes = useStyles()
  const ref = useRef()
  const history = useHistory()

  const [isCardHovered, setIsCardHovered] = useState(false)
  const [isTooltipHovered, setIsTooltipHovered] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const handleClick = () => {
    trackEvent(`Patient Copilot - copilot settings - item clicked`, {
      ...analyticsPayload,
      itemTitle: title
    })
    onClick()
  }

  const handleOpenPracticeGuidelines = () => {
    trackEvent(`Patient Copilot - copilot settings - practice guidelines clicked`, {
      ...analyticsPayload,
      itemTitle: title
    })

    history.push(ROUTES.PRACTICE_GUIDELINES)
  }
  useEffect(() => {
    if (isMobile() || !isCardHovered || isTooltipOpen) {
      return undefined
    }
    const timeoutId = setTimeout(() => {
      setIsTooltipOpen(true)
      trackEvent(`Patient Copilot - copilot settings - tooltip opened`, {
        ...analyticsPayload,
        itemTitle: title
      })
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [isCardHovered, isTooltipOpen, analyticsPayload, title])

  useEffect(() => {
    if (!isTooltipOpen || isCardHovered || isTooltipHovered) {
      return undefined
    }
    const timeoutId = setTimeout(() => setIsTooltipOpen(false), 200)
    return () => clearTimeout(timeoutId)
  }, [isTooltipOpen, isCardHovered, isTooltipHovered])

  return (
    <Grid
      container
      onClick={onClick}
      className={classes.copilotSettingItem}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
      ref={ref}
    >
      <CopilotSettingsTooltip
        anchorEl={ref.current}
        categoryData={categoryData}
        isOpen={isTooltipOpen}
        onViewPatientSettings={handleClick}
        onViewPracticeGuidelines={handleOpenPracticeGuidelines}
        onMouseEnter={() => setIsTooltipHovered(true)}
        onMouseLeave={() => setIsTooltipHovered(false)}
      />
      <div className={classes.greenDot}>
        <GreenDot />
      </div>
      <Grid item xs={12}>
        <IconAndText icon={icon} text={<DazzedParagraph14 strong>{title}</DazzedParagraph14>} />
      </Grid>
      {summary && (
        <Grid item xs={12}>
          <DazzedParagraph14 className={classes.summaryLabel}>{summary}</DazzedParagraph14>
        </Grid>
      )}
    </Grid>
  )
}

export default CopilotSettingItem
